import { ApiClientError } from "../api/client";

export type RequestFailureKind =
  | "network"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "unavailable"
  | "rateLimited"
  | "workspacePaused"
  | "api";

export type RecoveryAction =
  | "retry"
  | "refresh"
  | "signIn"
  | "goBack"
  | "goHome";

export type RequestFailureClassification = {
  kind: RequestFailureKind;
  messageKey: string | null;
  primary: RecoveryAction | null;
  requestId: string | null;
  secondary: RecoveryAction | null;
  status: number | null;
  tone: "danger" | "warning";
  useFullPage: boolean;
};

/** 客户端网络层失败，保留 requestId 供诊断与分类。 */
export class NetworkRequestError extends Error {
  readonly requestId: string;

  constructor(requestId: string = crypto.randomUUID()) {
    super("Network request failed");
    this.name = "NetworkRequestError";
    this.requestId = requestId;
  }
}

/** 请求超时（Abort），与网络断开区分展示。 */
export class RequestTimeoutError extends Error {
  readonly requestId: string;

  constructor(requestId: string = crypto.randomUUID()) {
    super("Request timed out");
    this.name = "RequestTimeoutError";
    this.requestId = requestId;
  }
}

const RATE_LIMITED_MESSAGE_KEYS = new Set([
  "database.retry",
  "tiktok.manual_sync_capacity",
  "tiktok.manual_sync_cooldown",
  "tiktok.rate_limited",
]);

const NOT_FOUND_MESSAGE_KEYS = new Set([
  "invitation.not_found",
  "member.not_found",
  "request.route_not_found",
  "user.not_found",
  "workspace.not_found",
  "workspace_role.not_found",
]);

const FORBIDDEN_MESSAGE_KEYS = new Set([
  "authorization.admin_forbidden",
  "authorization.workspace_forbidden",
  "workspace.super_admin_forbidden",
]);

const isFetchNetworkFailure = (error: unknown) => {
  if (!(error instanceof TypeError)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("fetch") ||
    message.includes("network") ||
    message.includes("load failed")
  );
};

const messageKeyFromApiError = (error: ApiClientError) =>
  error.payload.error.messageKey;

/** 把任意请求异常归一为可展示、可恢复的分类结果。 */
export const classifyRequestFailure = (
  error: unknown,
): RequestFailureClassification => {
  if (error instanceof NetworkRequestError) {
    return {
      kind: "network",
      messageKey: null,
      primary: "retry",
      requestId: error.requestId,
      secondary: "goHome",
      status: null,
      tone: "danger",
      useFullPage: true,
    };
  }

  if (error instanceof RequestTimeoutError) {
    return {
      kind: "unavailable",
      messageKey: "tiktok.sync_timeout",
      primary: "retry",
      requestId: error.requestId,
      secondary: "goHome",
      status: 504,
      tone: "danger",
      useFullPage: true,
    };
  }

  if (!(error instanceof ApiClientError)) {
    if (isFetchNetworkFailure(error)) {
      return {
        kind: "network",
        messageKey: null,
        primary: "retry",
        requestId: null,
        secondary: "goHome",
        status: null,
        tone: "danger",
        useFullPage: true,
      };
    }

    return {
      kind: "unavailable",
      messageKey: "service.unavailable",
      primary: "retry",
      requestId: null,
      secondary: "goHome",
      status: null,
      tone: "danger",
      useFullPage: false,
    };
  }

  const { status } = error;
  const messageKey = messageKeyFromApiError(error);
  const requestId = error.payload.requestId;

  if (status === 401 || messageKey === "auth.unauthorized") {
    return {
      kind: "unauthorized",
      messageKey,
      primary: "signIn",
      requestId,
      secondary: "goHome",
      status,
      tone: "danger",
      useFullPage: true,
    };
  }

  if (messageKey === "workspace.paused") {
    return {
      kind: "workspacePaused",
      messageKey,
      primary: "goHome",
      requestId,
      secondary: null,
      status,
      tone: "warning",
      useFullPage: false,
    };
  }

  if (
    status === 403 ||
    FORBIDDEN_MESSAGE_KEYS.has(messageKey)
  ) {
    return {
      kind: "forbidden",
      messageKey,
      primary: "goBack",
      requestId,
      secondary: "goHome",
      status,
      tone: "danger",
      useFullPage: false,
    };
  }

  if (
    status === 404 ||
    NOT_FOUND_MESSAGE_KEYS.has(messageKey)
  ) {
    return {
      kind: "notFound",
      messageKey,
      primary: "goBack",
      requestId,
      secondary: "goHome",
      status,
      tone: "danger",
      useFullPage: false,
    };
  }

  if (RATE_LIMITED_MESSAGE_KEYS.has(messageKey)) {
    return {
      kind: "rateLimited",
      messageKey,
      primary: "retry",
      requestId,
      secondary: null,
      status,
      tone: "warning",
      useFullPage: false,
    };
  }

  if (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    messageKey === "service.unavailable" ||
    messageKey.startsWith("tiktok.sync_") ||
    messageKey.startsWith("tiktok.upstream_") ||
    messageKey === "tiktok.authorization_unavailable" ||
    messageKey === "profile.unavailable" ||
    messageKey === "avatar.unavailable" ||
    messageKey === "workspace.data_unavailable"
  ) {
    return {
      kind: "unavailable",
      messageKey,
      primary: "retry",
      requestId,
      secondary: "goHome",
      status,
      tone: "danger",
      useFullPage: status === 503 || status === 504,
    };
  }

  return {
    kind: "api",
    messageKey,
    primary: status >= 500 ? "retry" : null,
    requestId,
    secondary: status >= 500 ? "goHome" : null,
    status,
    tone: "danger",
    useFullPage: false,
  };
};

export const failureTitleKey = (kind: RequestFailureKind) =>
  `errors.${kind}Title` as const;

export const failureDetailKey = (kind: RequestFailureKind) =>
  `errors.${kind}Detail` as const;

export const recoveryActionKey = (action: RecoveryAction) =>
  `errors.${action}` as const;
